<template>
  <div class="cc-page">
    <!-- ─── HEADER ─── -->
    <div class="cc-header shadow-sm">
      <div class="cc-header__info">
        <div class="cc-header__icon">
          <i class="fa fa-barcode"></i>
        </div>
        <div>
          <h1 class="cc-header__title">Conferência de Etiquetas (XML)</h1>
          <span class="cc-header__sub">Compras aguardando conferência física das etiquetas importadas</span>
        </div>
      </div>
      
      <div class="cc-header__controls">
        <div class="cc-refresh-control mr-3 d-none d-lg-flex">
          <div class="cc-refresh-info">
            <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i>
            <span>Auto-atualização: <strong>{{ refreshInterval }}s</strong></span>
          </div>
          <b-form-input
            v-model="refreshInterval"
            type="range"
            min="5"
            max="60"
            step="5"
            class="cc-refresh-slider"
            :disabled="carregando"
          />
        </div>
        
        <b-button variant="primary" pill class="px-4 font-weight-bold shadow-sm" @click="carregarCompras" :disabled="carregando">
          <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i> Atualizar
        </b-button>
      </div>
    </div>

    <!-- ─── CARDS DE RESUMO ─── -->
    <div class="cc-cards">
      <div class="cc-card cc-card--indigo shadow-xs">
        <div class="cc-card__num">{{ compras.length }}</div>
        <div class="cc-card__label">Compras Pendentes</div>
      </div>
      <div class="cc-card cc-card--amber shadow-xs">
        <div class="cc-card__num">{{ totalEtiquetasPendentes }}</div>
        <div class="cc-card__label">Etiquetas a Conferir</div>
      </div>
      <div class="cc-card cc-card--green shadow-xs">
        <div class="cc-card__num">{{ totalEtiquetasConferidas }}</div>
        <div class="cc-card__label">Etiquetas Coletadas</div>
      </div>
    </div>

    <!-- ─── FILTROS ─── -->
    <div class="cc-filtros">
      <div class="cc-filtros__search shadow-xs">
        <span class="cc-filtros__icon"><i class="fa fa-search"></i></span>
        <input
          v-model="filtro"
          class="cc-filtros__input"
          placeholder="Filtrar por fornecedor, compra ou nota..."
          @input="aplicarFiltros"
        />
      </div>
    </div>

    <!-- ─── TABELA ─── -->
    <div class="cc-table-card shadow-sm">
      <b-overlay :show="carregando" rounded="sm" opacity="0.6">
        <b-table
          :items="comprasFiltradas"
          :fields="campos"
          hover
          responsive
          show-empty
          empty-text="Nenhuma compra aguardando conferência de XML"
          class="cc-table mb-0"
          thead-class="cc-thead"
          @row-clicked="onRowClicked"
        >
          <!-- Compra # -->
          <template #cell(ID)="{ item }">
            <span class="cc-seq">#{{ item.ID }}</span>
          </template>

          <!-- Data -->
          <template #cell(DT_PEDIDO)="{ item }">
            <span class="cc-date">{{ formatarData(item.DT_PEDIDO) }}</span>
          </template>

          <!-- Fornecedor -->
          <template #cell(NOME_FORNECEDOR)="{ item }">
            <div class="cc-fornecedor">
              <div class="cc-fornecedor__avatar">{{ inicialFornecedor(item.NOME_FORNECEDOR) }}</div>
              <div>
                <div class="cc-fornecedor__nome">{{ item.NOME_FORNECEDOR || '—' }}</div>
                <div class="cc-fornecedor__nota">NF: {{ item.NOTA_FISCAL || '—' }}</div>
              </div>
            </div>
          </template>

          <!-- Progresso -->
          <template #cell(PROGRESSO)="{ item }">
            <div class="cc-prog-wrap">
              <div class="cc-prog-bar">
                <div
                  class="cc-prog-fill"
                  :style="{ width: calcProgresso(item) + '%' }"
                  :class="progressoFillClass(calcProgresso(item))"
                ></div>
              </div>
              <span class="cc-prog-pct">{{ item.TOTAL_CONFERIDO }} / {{ item.TOTAL_ETIQUETAS }}</span>
            </div>
          </template>

          <!-- Ação -->
          <template #cell(ACOES)="{ item }">
            <div class="d-flex justify-content-end gap-2">
              <button class="cc-btn-acao cc-btn-acao--indigo shadow-xs" @click.stop="abrirConferencia(item)" title="Coletar Etiquetas">
                <i class="fa fa-barcode mr-1"></i> Coletar
              </button>
              
              <button 
                class="cc-btn-acao cc-btn-acao--green shadow-xs" 
                @click.stop="finalizarConferencia(item)"
                :disabled="item.TOTAL_CONFERIDO == 0"
                title="Finalizar Conferência e Liberar Estoque"
              >
                <i class="fa fa-check-circle mr-1"></i> Finalizar
              </button>

              <button class="cc-btn-acao cc-btn-acao--amber shadow-xs" @click.stop="imprimirEspelho(item)" title="Imprimir Espelho da Conferência">
                <i class="fa fa-print mr-1"></i> Espelho
              </button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </div>

    <!-- Modais -->
    <compra-conferencia-modal
      v-if="selectedCompraId"
      ref="modalConferencia"
      :compra-id="selectedCompraId"
      @concluido="carregarCompras"
      @closed="onModalClosed"
    />
  </div>
</template>

<script>
import CompraConferenciaModal from './CompraConferenciaModal.vue'
import Tools from '@/tools/tools'
import CompraConferenciaReport from '@/utils/reports/CompraConferenciaReport'

export default {
  name: 'CompraConferenciaListagem',
  components: {
    CompraConferenciaModal
  },
  data () {
    return {
      carregando: false,
      compras: [],
      comprasFiltradas: [],
      filtro: '',
      selectedCompraId: null,
      refreshInterval: 15,
      refreshTimer: null,
      isModalOpen: false,
      campos: [
        { key: 'ID', label: 'Compra', sortable: true },
        { key: 'DT_PEDIDO', label: 'Data', sortable: true },
        { key: 'NOME_FORNECEDOR', label: 'Fornecedor', sortable: true },
        { key: 'NOTA_FISCAL', label: 'Nota Fiscal', sortable: true },
        { key: 'PROGRESSO', label: 'Progresso da Conferência', thStyle: { width: '300px' } },
        { key: 'ACOES', label: '', tdClass: 'text-right' }
      ]
    }
  },
  computed: {
    totalEtiquetasPendentes () {
      return this.compras.reduce((s, c) => s + (Number(c.TOTAL_ETIQUETAS) - Number(c.TOTAL_CONFERIDO)), 0)
    },
    totalEtiquetasConferidas () {
      return this.compras.reduce((s, c) => s + Number(c.TOTAL_CONFERIDO), 0)
    }
  },
  watch: {
    refreshInterval () {
      this.reiniciarTimer()
    }
  },
  created () {
    this.carregarCompras()
    this.iniciarTimer()
  },
  beforeDestroy () {
    this.pararTimer()
  },
  methods: {
    async carregarCompras () {
      if (this.carregando) return
      this.carregando = true
      try {
        const result = await this.$http.get('/api/protected/compra/conferencia/get-compras-pendentes')
        this.compras = result.data || []
        this.aplicarFiltros()
      } catch (err) {
        console.error('Erro ao carregar compras para conferência', err)
      } finally {
        this.carregando = false
      }
    },
    iniciarTimer () {
      this.pararTimer()
      this.refreshTimer = setInterval(() => {
        if (!this.isModalOpen) {
          this.carregarCompras()
        }
      }, this.refreshInterval * 1000)
    },
    pararTimer () {
      if (this.refreshTimer) clearInterval(this.refreshTimer)
    },
    reiniciarTimer () {
      this.iniciarTimer()
    },
    aplicarFiltros () {
      let lista = [...this.compras]
      if (this.filtro.trim()) {
        const f = this.filtro.toLowerCase()
        lista = lista.filter(c =>
          String(c.ID).includes(f) ||
          (c.NOME_FORNECEDOR || '').toLowerCase().includes(f) ||
          (c.NOTA_FISCAL || '').toLowerCase().includes(f)
        )
      }
      this.comprasFiltradas = lista
    },
    onRowClicked (item) {
      this.selectedCompraId = item.ID
    },
    calcProgresso (compra) {
      const t = Number(compra.TOTAL_ETIQUETAS) || 0
      const c = Number(compra.TOTAL_CONFERIDO) || 0
      if (t === 0) return 0
      return Math.min(100, Math.round((c / t) * 100))
    },
    progressoFillClass (pct) {
      if (pct >= 100) return 'cc-prog-fill--green'
      if (pct >= 50) return 'cc-prog-fill--amber'
      return 'cc-prog-fill--indigo'
    },
    formatarData (v) {
      if (!v) return '—'
      return new Date(v).toLocaleDateString('pt-BR')
    },
    inicialFornecedor (nome) {
      if (!nome) return '?'
      return nome.trim().charAt(0).toUpperCase()
    },
    abrirConferencia (compra) {
      this.selectedCompraId = compra.ID
      this.isModalOpen = true
      this.$nextTick(() => {
        this.$refs.modalConferencia.abrir()
      })
    },
    onModalClosed () {
      this.isModalOpen = false
      this.selectedCompraId = null
    },
    async finalizarConferencia(compra) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Deseja finalizar a conferência da compra #${compra.ID}? Todas as etiquetas coletadas serão liberadas para o estoque disponível.`,
        {
          title: 'Finalizar Conferência',
          okTitle: 'SIM, FINALIZAR',
          cancelTitle: 'CANCELAR',
          okVariant: 'success',
          centered: true
        }
      )
      
      if (!confirm) return
      
      this.carregando = true
      try {
        const response = await this.$http.post('/api/protected/compra/conferencia/finalizar', {
          pedidoCompraId: compra.ID,
          login: window.localStorage.getItem('login')
        })
        
        if (response.data.sucesso) {
          Tools.exibirMensagem(this.$store, 'Concluído', 'success', 'Conferência finalizada com sucesso!')
          this.carregarCompras()
        }
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.carregando = false
      }
    },
    async imprimirEspelho(compra) {
      CompraConferenciaReport.imprimir(compra, this.$http, this.$store)
    }
  }
}
</script>

<style scoped>
.cc-page {
  padding: 24px;
  background: #f8fafc;
  min-height: calc(100vh - 60px);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

/* HEADER */
.cc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
}
.cc-header__info { display: flex; align-items: center; gap: 16px; }
.cc-header__icon {
  width: 52px; height: 52px;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; color: #fff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}
.cc-header__title { font-size: 1.5rem; font-weight: 800; color: #1e293b; margin: 0; letter-spacing: -0.02em; }
.cc-header__sub { color: #64748b; font-size: 0.9rem; }

.cc-header__controls { display: flex; align-items: center; }
.cc-refresh-control { display: flex; flex-direction: column; min-width: 160px; }
.cc-refresh-info { font-size: 0.75rem; color: #64748b; margin-bottom: 6px; display: flex; justify-content: space-between; }
.cc-refresh-slider { height: 5px; padding: 0; }

/* CARDS */
.cc-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}
.cc-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  border-bottom: 4px solid transparent;
  transition: all 0.2s;
}
.cc-card:hover { transform: translateY(-4px); box-shadow: 0 10px 20px rgba(0,0,0,0.05); }
.cc-card--indigo { border-color: #6366f1; }
.cc-card--amber  { border-color: #f59e0b; }
.cc-card--green  { border-color: #10b981; }
.cc-card__num { font-size: 2.25rem; font-weight: 800; color: #1e293b; line-height: 1; }
.cc-card__label { color: #64748b; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 8px; }

/* FILTROS */
.cc-filtros { margin-bottom: 20px; }
.cc-filtros__search { position: relative; max-width: 400px; }
.cc-filtros__icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.cc-filtros__input {
  width: 100%; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 16px 12px 44px;
  font-size: 0.95rem; outline: none; transition: all 0.2s;
}
.cc-filtros__input:focus { border-color: #6366f1; box-shadow: 0 0 0 4px rgba(99,102,241,0.1); }

/* TABELA */
.cc-table-card { background: #fff; border-radius: 16px; overflow: hidden; }
.cc-thead th {
  background: #f8fafc !important; color: #64748b !important; font-size: 0.75rem !important;
  text-transform: uppercase !important; letter-spacing: 0.05em !important; font-weight: 700 !important;
  border-bottom: 1px solid #e2e8f0 !important; padding: 16px !important;
}
.cc-seq { font-family: 'JetBrains Mono', monospace; font-weight: 700; color: #4f46e5; }
.cc-fornecedor { display: flex; align-items: center; gap: 12px; }
.cc-fornecedor__avatar {
  width: 36px; height: 36px; background: #eef2ff; color: #4f46e5;
  border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800;
}
.cc-fornecedor__nome { font-weight: 600; color: #1e293b; font-size: 0.95rem; }
.cc-fornecedor__nota { color: #94a3b8; font-size: 0.75rem; }

.cc-prog-wrap { display: flex; align-items: center; gap: 12px; }
.cc-prog-bar { flex: 1; height: 8px; background: #f1f5f9; border-radius: 10px; overflow: hidden; }
.cc-prog-fill { height: 100%; border-radius: 10px; transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1); }
.cc-prog-fill--indigo { background: #6366f1; }
.cc-prog-fill--amber  { background: #f59e0b; }
.cc-prog-fill--green  { background: #10b981; }
.cc-prog-pct { font-size: 0.8rem; font-weight: 600; color: #475569; min-width: 60px; }

.cc-btn-acao {
  border: none; border-radius: 10px; padding: 8px 16px;
  font-weight: 700; font-size: 0.85rem; cursor: pointer; transition: all 0.2s;
  display: flex; align-items: center; justify-content: center;
}

.cc-btn-acao--indigo { background: #eef2ff; color: #4f46e5; }
.cc-btn-acao--indigo:hover { background: #4f46e5; color: #fff; }

.cc-btn-acao--green { background: #ecfdf5; color: #10b981; }
.cc-btn-acao--green:hover { background: #10b981; color: #fff; }

.cc-btn-acao--amber { background: #fffbeb; color: #f59e0b; }
.cc-btn-acao--amber:hover { background: #f59e0b; color: #fff; }

.cc-btn-acao:disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }

.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.gap-2 { gap: 8px; }
</style>



// WEBPACK FOOTER //
// src/views/movimentos/compras/CompraConferenciaListagem.vue